/* eslint-disable no-unreachable */
import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "@/styles/fonts.css";
import "@/styles/typography-scale.css";
import "@/styles/custom.css";

// Root layout
import Layout from "@/pages/admin/Layout";
// Context
import { SurveyProvider } from "@/context/SurveyContext";

// Pages (ensure these paths/names exist in your repo)
import NotFound from "@/pages/NotFound";
import Home from "@/pages/Home";
import BrandGuide from "@/pages/BrandGuide";

// Admin area
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminHub from "@/pages/admin/AdminHub";
import SurveyOverview from "@/pages/admin/SurveyOverview";
import SurveyBuilder from "@/pages/admin/surveyMaker/SurveyBuilder";
import SurveyPreview from "@/pages/admin/surveyMaker/SurveyPreview";
import SurveyManager from "@/pages/admin/surveyMaker/SurveyManager";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReportsManager from "@/pages/ReportsManager";
import ReportCreator from "@/pages/admin/reports/ReportCreator";
import MonitoringManager from "@/pages/admin/MonitoringManager"
import CreateUser from "@/pages/admin/users/CreateUser";
import ManageUsers from "@/pages/admin/users/ManageUsers";
import ReportMonitor from "@/pages/admin/reports/ReportMonitor";
import SurveyMonitor from "@/pages/admin/monitoring/SurveyMonitor"
import ManageHospiceLists from "@/pages/admin/users/ManageHospiceLists";
import HospiceUsersList from "@/pages/admin/users/HospiceUsersList";
import UserReportActivityDashboard from "@/pages/admin/monitoring/UserReportActivityDashboard";

import Logout from "@/components/Logout";
import AuthCallback from "@/components/AuthCallback";
import IansUploadPage from "@/pages/IansUploadPage";
import PlatformConfigurationManager from "./pages/admin/users/PlatformConfigurationManager";
import FileUploadPage from "./pages/admin/configuration/FileUploadPage";
import ManageRoles from "./pages/admin/users/ManageRoles";
import EmailTemplatesPage from "./pages/admin/configuration/EmailTemplatesPage";
// MSAL imports for Azure/Entra ID authentication
import { MsalProvider } from "@azure/msal-react";
import { msalInstance, initializeMsal } from "./authConfig";
import SafeLocalStorage from "@/utils/safeLocalStorage";
import { STORAGE_KEYS } from "@/constants/storage";
import TagManager from "react-gtm-module";

// Initialize Google Tag Manager
const tagManagerArgs = {
  gtmId: 'GTM-N6MX97N'
};

// User loader function - for MSAL authentication
export async function usersLoader() {
  try {
    // For MSAL authentication, user info comes from Azure tokens, not API
    // The MSAL provider handles authentication state
    const authSession = SafeLocalStorage.get(STORAGE_KEYS.AUTH_SESSION);
    if (!authSession || !authSession.isAuthenticated) {
      console.warn("No authenticated session found - user needs to login via Azure");
      return null;
    }
    console.log("Authenticated user found:", authSession.userInfo?.username || authSession.accountId);
    return authSession; // Return the full auth session with user info
  } catch (error) {
    console.warn("User loader failed, continuing without user:", error);
    return null;
  }
}

// Router config
const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: "brand-guide", element: <BrandGuide /> },
      {path: "secret", element: <IansUploadPage />}, // hidden page for file uploads
      {
        id: "admin",
        path: "admin",
        element: (
          <SurveyProvider>
            <AdminLayout />
          </SurveyProvider>
        ),
        loader: usersLoader,
        errorElement: <ErrorBoundary />,
        children: [
          { index: true, element: <AdminHub /> },
          { path: "surveyManager", element: <SurveyOverview /> },
          {path: "reportsManager", element: <ReportsManager />},
          {path: "configManager", element: <PlatformConfigurationManager />},
          {
            path: "surveyMaker",
            element: <Outlet />,
            children: [
              { path: "manage", element: <SurveyManager /> },
              { path: "builder", element: <SurveyBuilder /> },
              { path: "preview", element: <SurveyPreview /> },
            ],
          },
          { path: "users", element: <Outlet />,
            children: [
              { path: "create", element: <CreateUser /> },
              { path: "manage", element: <ManageUsers /> },
              { path: "permissions", element: <ManageRoles /> },
              { path: "hospiceLists", element: <ManageHospiceLists /> },
              { path: "hospiceUsers", element: <HospiceUsersList /> },
            ],
           },
           { path: "reports", element: <Outlet />,
            children: [
              { path: "reportCreator", element: <ReportCreator /> },
              { path: "reportMonitor", element: <ReportMonitor /> },
            ]
            },
          { path: "monitoring", element: <Outlet />,
            children: [
              { index: true, element: <MonitoringManager /> },
              { path: "surveyMonitor", element: <SurveyMonitor /> },
              { path: "userReportActivity", element: <UserReportActivityDashboard /> }
            ]
          }, 
          { path: "configuration", element: <Outlet />,
            children: [
              { path: "fileUpload", element: <FileUploadPage /> },
              { path: "emailTemplatesPage", element: <EmailTemplatesPage /> }

            ]
          },
        ],
      },
      { path: "auth/callback", element: <AuthCallback /> },
      { path: "*", element: <NotFound /> },
    ],
  }
]);

// Initialize app
async function startApp() {
  // Initialize GTM
  TagManager.initialize(tagManagerArgs);
  if (import.meta.env.DEV) {
    console.log('Google Tag Manager initialized with ID:', tagManagerArgs.gtmId);
  }

  // Fetch auth config from API first (if not on localhost and env vars not set)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocalhost && !import.meta.env.VITE_AZURE_CLIENT_ID) {
    try {
      if (import.meta.env.DEV) {
        console.log("Fetching auth config from API...");
      }
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
      const configResponse = await fetch(`${apiBaseUrl}/clientconfiguration`);
      if (configResponse.ok) {
        const authConfig = await configResponse.json();
        if (import.meta.env.DEV) {
          console.log("Auth config received from API");
        }
        // Store in sessionStorage so authConfig.js can use it
        sessionStorage.setItem('__authConfigFromApi', JSON.stringify(authConfig));
        window.__authConfigFromApi = authConfig;
      } else {
        console.warn("Failed to fetch auth config from API, will use env vars");
      }
    } catch (error) {
      console.warn("Could not fetch auth config from API:", error);
    }
  }

  // INITIALIZE GOOGLE ANALYTICS
  // ReactGA.pageview(window.location.pathname + window.location.search);

  // Initialize MSAL
  try {
    if (import.meta.env.DEV) {
      console.log("Starting Admin Portal...");
    }
    await initializeMsal();
    // Store msalInstance globally for components to use
    window.msalInstance = msalInstance;
    if (import.meta.env.DEV) {
      console.log("MSAL initialized, checking for redirect response...");
    }
    
    // Handle redirect response immediately after initialization
    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse) {
      if (import.meta.env.DEV) {
        console.log("Redirect response handled - Full account object:", redirectResponse.account);
        console.log("ID Token Claims:", redirectResponse.idTokenClaims);
        console.log("Account details:", {
          username: redirectResponse.account?.username,
          name: redirectResponse.account?.name,
          idTokenName: redirectResponse.idTokenClaims?.name,
          preferredUsername: redirectResponse.idTokenClaims?.preferred_username,
          accessToken: redirectResponse.accessToken ? 'Present' : 'Missing'
        });
      }
      // Try to call backend API now that we have tokens
      try {
        if (import.meta.env.DEV) {
          console.log("main.jsx: Attempting backend API call...");
        }
        
        // Import callApi dynamically to avoid circular dependencies
        const { callApi } = await import("./utils/callApi");
        
        const backendResponse = await callApi(
          `${import.meta.env.VITE_API_URL}/api/welcomemessage`,
          { method: 'GET' },
          msalInstance,
          [redirectResponse.account]
        );
        
        if (import.meta.env.DEV) {
          console.log("main.jsx: Backend API call successful:", backendResponse);
        }
        
        // Store the welcome message and user data with full account info
        const authSession = {
          isAuthenticated: true,
          lastLogin: new Date().toISOString(),
          accountId: redirectResponse.account.homeAccountId,
          hasApiAccess: true,
          name: redirectResponse.account.name, // User's display name at top level for easy access
          username: redirectResponse.account.username,
          email: redirectResponse.account.username,
          userInfo: {
            username: redirectResponse.account.username,
            name: redirectResponse.account.name,
            email: redirectResponse.account.username
          }
        };
        SafeLocalStorage.set(STORAGE_KEYS.WELCOME_MESSAGE, backendResponse);
        SafeLocalStorage.set(STORAGE_KEYS.AUTH_SESSION, authSession);
        
        if (import.meta.env.DEV) {
          console.log("User data stored:", {
            name: authSession.name,
            email: authSession.email,
            accountId: authSession.accountId
          });
          console.log("Navigating to /admin...");
        }
        // Use window.location to navigate since router isn't mounted yet
        window.location.href = '/admin';
        
      } catch (apiError) {
        if (import.meta.env.DEV) {
          console.warn("main.jsx: Backend API call failed (this might be expected):", apiError);
          console.warn("Backend might not be ready or endpoint doesn't exist yet");
        }
      }
      
    } else {
      if (import.meta.env.DEV) {
        console.log("No redirect response (normal for initial page load)");
      }
    }
    
  } catch (error) {
    // Ignore harmless "no_token_request_cache_error" - this is expected on first load
    if (error.errorCode === 'no_token_request_cache_error') {
      if (import.meta.env.DEV) {
        console.log("No cached token request (normal for initial page load)");
      }
      // Continue to render app below
    } else {
      console.error("Failed to initialize MSAL:", error);
      
      // Show error to user
      const root = ReactDOM.createRoot(document.getElementById("root"));
      root.render(
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Authentication Setup Error</h2>
          <p>Failed to initialize Microsoft authentication.</p>
          <p><span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Error:</span> {error.message}</p>
          <p>Please check your Azure AD configuration and try refreshing the page.</p>
        </div>
      );
      return;
    }
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <RouterProvider 
          router={router} 
          fallbackElement={<div>Loading…</div>}
          hydrateFallback={<div>Loading application...</div>}
        />
      </MsalProvider>
    </React.StrictMode>
  );
}

startApp().catch((err) => {
  console.error("Failed to start app", err);
});
